import { h, defineComponent } from 'vue';
import { Button } from '@/ui/components/Button';
import { t } from '@/ui/i18n/i18n';
import { SupportedExportFormat, SupportedExportFormatEnum } from '@/services/api/resources/inspection/exports';
import { isOnFreePlan } from '@/ui/auth/auth';
// @ts-ignore
import { BDropdown, BDropdownItem } from 'buefy/dist/esm/dropdown';
import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
import { StarredAdItem } from '@/ui/components/upgrade/StarredAdItem';
import { inspectionExportTypes } from '@/ui/pages/inspection/export/constants';
import { starsIcon } from '@/ui/components/icon/icons';
import { Icon } from '@/ui/components/icon/Icon';
import { Tooltip } from '@/ui/components/Tooltip';

interface InspectionReportExportProps {
  loading: boolean;
  hasPhotos: boolean;
  hasPdf: boolean;
  iconized?: boolean;
  tooltiped?: boolean;
  unlabeled?: boolean;
  formats: Array<SupportedExportFormatEnum>;
}

export const InspectionReportExport = defineComponent<InspectionReportExportProps>({
  name: 'InspectionReportExport',
  props: ['loading', 'hasPhotos', 'hasPdf', 'iconized', 'tooltiped', 'unlabeled', 'formats'],
  setup(props, context) {
    const onClickExport = (format: SupportedExportFormat | 'photos') => {
      context.emit('export', { format });
    };
    const exportFormatButtonLabelMap: Map<SupportedExportFormatEnum, string> = new Map([
      [SupportedExportFormatEnum.XLSX_SIMPLE, inspectionExportTypes.excelSimple],
      [SupportedExportFormatEnum.XLSX, inspectionExportTypes.excel],
      [SupportedExportFormatEnum.CSV, inspectionExportTypes.csv],
      [SupportedExportFormatEnum.DOCX, inspectionExportTypes.word],
    ]);

    return function render() {
      const additionalFormats = props.formats
        .filter(format => exportFormatButtonLabelMap.has(format))
        .map(format => {
          return h(
            BDropdownItem,
            { on: { click: () => onClickExport(format) } }, [exportFormatButtonLabelMap.get(format)],
          );
        });

      const starredAdEntry = (text: string) => h(StarredAdItem, {
        class: 'inspection-report-export-ad__entry', props: { isStarred: true },
      }, text);

      //lazy load if not a free plan
      const additionalFormatsAd = () => [
        h(UpgradeAd,
          {
            class: 'upgrade-ad--inspection-report-export inspection-report-export-ad',
            props: {
              message: t('inspections.export-ad'),
              withDropdown: true,
            },
          },
          [
            h(
              'div',
              { class: 'inspection-report-export-ad__fields', slot: 'fields' },
              props.formats
                .filter(format => exportFormatButtonLabelMap.has(format))
                .map(format => {
                  return starredAdEntry(exportFormatButtonLabelMap.get(format) || '');
                }),
            ),
          ]),
      ];

      const dropdownTrigger = ({ active }: { active: boolean }) => h('div', [
        h(
          Button, {
            class: `inspection-report-export-btn ${props.iconized ? 'inspection-report-export-btn--iconized' : ''}`,
            props: { loading: props.loading, type: props.iconized ? 'is-text' : '' },
            attrs: {
              iconRight: active ? 'angle-up' : 'angle-down',
            },
          }, [
            isOnFreePlan.value ? starsIcon() : null,
            h(Icon, {
              props: {
                classes: 'inspection-report-export-btn__download-icon',
                icon: 'download',
              },
            }),
            !props.iconized && !props.unlabeled && h('div', [t('common.export')]),
          ],
        ),
      ]);

      const dropdownTriggerWithTooltip = ({ active }: { active: boolean }) => h(Tooltip, {
        props: {
          label: t('report-download.export'),
          position: 'is-top',
        },
      }, [dropdownTrigger({ active })]);

      return h('div', { class: 'inspection-report-export' }, [
        h(
          BDropdown,
          {
            class: 'inspection-report-export-dropdown',
            attrs: { position: 'is-bottom-left' },
            scopedSlots: {
              trigger: props.tooltiped ? dropdownTriggerWithTooltip : dropdownTrigger,
            },
          },
          [
            props.hasPdf
              ? h(BDropdownItem, { on: { click: () => onClickExport('pdf') } }, [inspectionExportTypes.pdf])
              : null,
            props.hasPhotos
              ? h(BDropdownItem, { on: { click: () => onClickExport('photos') } }, [inspectionExportTypes.images])
              : null,
            ...(isOnFreePlan.value ? additionalFormatsAd() : additionalFormats),
          ],
        )]);
    };
  },
});
